import * as React from 'react';
import s from './InvoicePay.scss';
import { Money } from '@wix/crm-financial-formatters';
import Decimal from 'decimal.js';
import { CashierPaymentsWidgetLazy } from '@wix/cashier-payments-widget/lazy';
import { ICashierPaymentsProps } from '@wix/cashier-payments-widget';
import { WithTranslation, withTranslation } from 'react-i18next';
import get from 'lodash.get';
import {
  InvoicePayData,
  InvoicePayMainSection,
} from './InvoicePayMainSection/InvoicePayMainSection';
import { InvoicePayHeader } from './InvoicePayHeader/InvoicePayHeader';
import { RecurringPayView } from './RecurringPayView/RecurringPayView';
import { isMobileView } from '../../api/isMobileView';
import { ErrorBoundary } from '../../api/error-reporter/error-reporter';
import { CashierEmptyState } from './CashierEmptyState/CashierEmptyState';
import { CustomizedPayButton } from './CustomizePay/CustomizePay';
import { SimpleInvoiceSettings } from '../../routes/handlers/invoiceSettings';

function omitFalsies(obj: object) {
  return Object.keys(obj).reduce((newObj, key) => {
    // @ts-expect-error
    const value = obj[key];
    if (!value && value !== false) {
      return newObj;
    }
    return {
      [key]: value,
      ...newObj,
    };
  }, {});
}

export interface InvoicePayProps extends WithTranslation {
  invoice: InvoicePayData;
  setLocation(url: string): void;
  cashierPaymentsProps: ICashierPaymentsProps;
  backUrl: string;
  successUrl: string;
  isRecurringInvoice: boolean;
  businessEmail?: string;
  isOnlyOfflinePayment?: boolean;
  customizePayMethod?: () => Promise<any>;
  errorUrl?: string;
  invoiceSettings: SimpleInvoiceSettings;
}

interface InvoicePayState {
  cashierPaymentsProps: ICashierPaymentsProps;
}

class InvoicePayComponent extends React.Component<
  InvoicePayProps,
  InvoicePayState
> {
  constructor(props: InvoicePayProps) {
    super(props);

    this.state = {
      cashierPaymentsProps: null,
    };

    setTimeout(() => {
      this.props.setLocation(props.backUrl);
    }, 15 * 60 * 1000);
  }

  componentDidMount() {
    const { invoice, isRecurringInvoice, isOnlyOfflinePayment } = this.props;
    const cashierPaymentsProps: ICashierPaymentsProps = this.props
      .cashierPaymentsProps && {
      paymentMethodChanged: (paymentMethodId) => void 0,
      ...this.props.cashierPaymentsProps,
      theme: 'default',
      allowRecurringPaymentOnly: isRecurringInvoice,
      allowManualPayment: true,
      walletPaymentInNewTab: false,
      onPaymentComplete: (method, data) => {
        if (
          data.clientStatus === 'Approved' ||
          data.clientStatus === 'Pending'
        ) {
          this.props.setLocation(this.props.successUrl);
        }
      },
    };
    const field = omitFalsies({
      email: invoice.customer.email,
      countryCode: get(invoice.customer, 'address.country'),
      firstName: invoice.customer.firstName,
      lastName: invoice.customer.lastName,
      address:
        get(invoice.customer, 'address.description') ||
        get(invoice.customer, 'address.street'),
      street: get(invoice.customer, 'address.street'),
      city: get(invoice.customer, 'address.city'),
      state: get(invoice.customer, 'address.state'),
      zipCode: get(invoice.customer, 'address.zip'),
      phone: invoice.customer.phone,
    });
    if (isOnlyOfflinePayment) {
      this.setState({
        cashierPaymentsProps: {
          ...cashierPaymentsProps,
          mandatoryFields: field,
        },
      });
    } else {
      this.setState({
        cashierPaymentsProps: {
          ...cashierPaymentsProps,
          mandatoryFieldsPrefilled: field,
        },
      });
    }
  }

  render() {
    const {
      t,
      invoice,
      backUrl,
      isRecurringInvoice,
      businessEmail,
      customizePayMethod,
      errorUrl,
      invoiceSettings,
    } = this.props;
    const { currency: currencyCode, locale } = invoice;
    const currencyFormatter = (amountToPay: Decimal.Value) =>
      Money({ locale, currencyCode, amount: new Decimal(amountToPay) })
        .formatted;
    const hasCustomPayMethod = !!customizePayMethod;

    return isMobileView() ? (
      <div className={s.paymentPageMobile}>
        <div className={s.summaryContainer}>
          <div className={s.summary} data-hook="payment-summary">
            {t('quoteview.uou_payment_summary')}
            <div
              className={`${s.value} ${s.balance}`}
              data-hook="balance-value"
            >
              {currencyFormatter}
            </div>
          </div>
        </div>
        <div className={s.paymentContainer}>
          <ErrorBoundary fallback={<CashierEmptyState />}>
            {this.state.cashierPaymentsProps && (
              <CashierPaymentsWidgetLazy {...this.state.cashierPaymentsProps} />
            )}
            {hasCustomPayMethod && (
              <CustomizedPayButton
                customizePayMethod={customizePayMethod}
                backUrl={backUrl}
                errorUrl={errorUrl}
              />
            )}
            <div className={s.securityIcon} />
          </ErrorBoundary>
        </div>
      </div>
    ) : (
      <div className={s.paymentPage}>
        <InvoicePayHeader backUrl={backUrl} />

        <div
          className={`${s.paymentSection} ${
            !isRecurringInvoice && s.payDefaults
          }`}
        >
          {isRecurringInvoice && (
            <RecurringPayView invoice={invoice} currency={currencyFormatter} />
          )}
          <InvoicePayMainSection
            invoice={invoice}
            currencyFormatter={currencyFormatter}
            cashierPaymentsProps={this.state.cashierPaymentsProps}
            businessEmail={isRecurringInvoice && businessEmail}
            customizePayMethod={customizePayMethod}
            backUrl={backUrl}
            errorUrl={errorUrl}
            invoiceSettings={invoiceSettings}
          />
        </div>
      </div>
    );
  }
}

export const InvoicePay = withTranslation()(InvoicePayComponent);
